import { useState, useCallback, useEffect, useRef } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/format";
import { useCountdown } from "@/lib/useCountdown";
import { DropZone } from "@/components/undertake/DropZone";
import { UploadProgress, useProofUploads } from "@/components/undertake/UploadProgress";
import { ProofButton } from "@/components/undertake/ProofButton";
import { X } from "lucide-react";
import { toast } from "sonner";

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export function FileList({ files, onRemove }) {
  if (!files.length) return null;
  return (
    <ul className="mt-4 flex flex-col gap-2" data-testid="undertake-file-list">
      {files.map((f) => (
        <li key={f.id} data-testid={`undertake-file-${f.id}`} className="flex items-center justify-between gap-3 px-3 py-2 text-xs" style={{ border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
          <span className="truncate">{f.name} · {fmtSize(f.size)}</span>
          <button onClick={() => onRemove(f)} data-testid={`undertake-file-remove-${f.id}`} className="opacity-60 hover:opacity-100"><X size={14} /></button>
        </li>
      ))}
    </ul>
  );
}

export function UndertakeModal({ item, onClose }) {
  const [u, setU] = useState(item);
  const [busy, setBusy] = useState(false);
  const accepted = u.status === "undertaken";
  const expire = useCallback(() => { toast("time ran out"); onClose(); }, [onClose]);
  const left = useCountdown(accepted ? u.deadline : null, expire);
  const acceptedRef = useRef(accepted);
  acceptedRef.current = accepted;

  const pendingRelease = useRef(null);
  useEffect(() => {
    // StrictMode (dev) unmounts and remounts effects; defer so a remount can cancel the release
    if (pendingRelease.current) { clearTimeout(pendingRelease.current); pendingRelease.current = null; }
    const release = () => {
      if (acceptedRef.current) return;
      const token = localStorage.getItem("absorb_token");
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/undertakings/${item.id}/release`, { method: "POST", keepalive: true, headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    };
    window.addEventListener("beforeunload", release);
    return () => { window.removeEventListener("beforeunload", release); pendingRelease.current = setTimeout(release, 0); };
  }, [item.id]);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const exit = async () => {
    if (uploading > 0 && !window.confirm("an upload is still running. leave and cancel it?")) return;
    if (!accepted) await api.post(`/undertakings/${u.id}/release`).catch((e) => console.debug("release failed", e));
    onClose();
  };

  const accept = () => run(async () => {
    const { data } = await api.post(`/undertakings/${u.id}/accept`);
    setU((p) => ({ ...p, ...data }));
    toast.success("undertaken · 24 hours start now");
  });

  const { items: progress, upload, busy: uploading } = useProofUploads("undertaking", u.id, (data) => setU((p) => ({ ...p, files: [...(p.files || []), data] })));

  const remove = (f) => run(async () => {
    await api.delete(`/undertakings/${u.id}/files/${f.id}`);
    setU((p) => ({ ...p, files: p.files.filter((x) => x.id !== f.id) }));
  });

  const sendUp = () => run(async () => {
    await api.post(`/undertakings/${u.id}/send-up`);
    toast.success("submitted for review");
    onClose();
  });

  const files = u.files || [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in" style={{ background: "rgba(0,0,0,0.75)" }} data-testid="undertake-modal-overlay">
      <div className="w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto" style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }} data-testid="undertake-modal">
        <div className="flex justify-between items-start mb-6 gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs tracking-widest" style={{ color: "var(--accent)" }} data-testid="undertake-modal-reward">{u.provisions} provisions</span>
          </div>
          <button onClick={exit} data-testid="undertake-exit-btn" className="text-sm opacity-60 hover:opacity-100 shrink-0" style={{ color: "var(--text-primary)" }}>[ exit ]</button>
        </div>
        <p className="long-text font-head font-light text-xl leading-relaxed mb-2" style={{ color: "var(--text-primary)" }} data-testid="undertake-modal-text">{u.text}</p>
        <p className="text-xs mb-8" style={{ color: "var(--text-muted)" }}>delegated by {u.author?.name || "unknown"}</p>
        {!accepted ? (
          <>
            <p className="text-xs mb-6" style={{ color: "var(--text-muted)" }}>accept and you have exactly 24 hours to complete it.</p>
            <button onClick={accept} disabled={busy} data-testid="undertake-accept-btn" className="btn w-full py-3 text-sm tracking-widest">{busy ? "…" : "ACCEPT"}</button>
          </>
        ) : (
          <>
            <div className="flex justify-between text-xs tracking-widest mb-3" style={{ color: "var(--text-muted)" }}>
              <span className="flex items-center gap-2"><ProofButton testId="undertake-proof-btn" /> · seen by the delegator and the members</span>
              <span data-testid="undertake-countdown" style={{ color: "var(--accent)" }}>{left}</span>
            </div>
            <DropZone onFiles={upload} disabled={busy} />
            <p className="text-[10px] tracking-widest mt-2" style={{ color: "var(--text-muted)" }}>up to 10 files · 500 mb each · keep this window open while uploading</p>
            {uploading > 0 && <p className="text-xs mt-3" style={{ color: "var(--text-muted)" }} data-testid="undertake-uploading">uploading {uploading}…</p>}
            <UploadProgress items={progress} />
            <FileList files={files} onRemove={remove} />
            <button onClick={sendUp} disabled={busy || uploading > 0 || files.length === 0} data-testid="undertake-send-up-btn" className="btn w-full py-3 mt-6 text-sm tracking-widest">
              {busy ? "…" : "SUBMIT FOR REVIEW"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
